import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './MiddelenModule.module.css';
import { Project } from '../../types';
import { Button } from '../common/Button/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import {
  analysisAPI,
  personasAPI,
  strategyAPI,
  tasksAPI,
  questionsAPI,
  relationshipsAPI,
  sentimentAPI,
  mediaAPI,
  aiAssistantAPI,
} from '../../services/api';

// ── Types ──────────────────────────────────────────────────────────────────

type CommunicationTool = 'brief' | 'email' | 'sms' | 'video' | 'socialmedia' | 'infographic' | 'poster' | 'website' | 'persbericht';
type SituationType = 'works' | 'hinder' | 'progress' | 'milestone' | 'accessibility' | 'meeting' | 'decision' | 'urgent' | 'participation' | 'feedback';
type CommunicationGoal = 'inform' | 'prepare' | 'warn' | 'reduce_concerns' | 'understanding' | 'invite_participation' | 'collect_input' | 'share_results' | 'accountability';
type ImpactLevel = 'low' | 'moderate' | 'strong' | 'politically_sensitive' | 'urgent_crisis';

type ViewMode = 'choose' | 'smart';

interface ProjectData {
  gebiedsanalyse: any;
  personas: any[];
  strategies: any[];
  tasks: any[];
  questions: any[];
  relationships: any[];
  sentiment: any;
  media: any[];
}

interface MissingInfo {
  field: string;
  source: string;
}

interface SmartContext {
  situation: SituationType;
  situationReason: string;
  goal: CommunicationGoal;
  goalReason: string;
  impact: ImpactLevel;
  impactReason: string;
  suggestedTools: { id: CommunicationTool; reason: string; primary: boolean }[];
  targetGroupHints: string[];
  missingInfo: MissingInfo[];
  contentBlocks: string[];
}

interface MiddelenModuleProps {
  project: Project;
}

// ── Tool definitions ───────────────────────────────────────────────────────

const TOOLS: Record<CommunicationTool, { label: string; description: string; color: string; icon: React.ReactNode }> = {
  brief: {
    label: 'Brief',
    description: 'Formele bewonersbrief per post',
    color: 'var(--color-content-primary)',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/></svg>,
  },
  email: {
    label: 'E-mail',
    description: 'Digitale nieuwsbrief',
    color: 'var(--color-alt-content-link)',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/></svg>,
  },
  sms: {
    label: 'SMS / WhatsApp',
    description: 'Korte tekstberichten',
    color: 'var(--color-success)',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
  },
  video: {
    label: 'Video script',
    description: 'Videoboodschap script',
    color: 'var(--color-error)',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>,
  },
  socialmedia: {
    label: 'Social media',
    description: 'Posts voor sociale media',
    color: 'var(--color-warning)',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>,
  },
  infographic: {
    label: 'Infographic',
    description: 'Visuele informatie',
    color: '#9C27B0',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  },
  poster: {
    label: 'Poster / flyer',
    description: 'Fysieke aankondigingen',
    color: '#FF9800',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
  },
  website: {
    label: 'Website update',
    description: 'Online nieuwsbericht',
    color: '#00BCD4',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  },
  persbericht: {
    label: 'Persbericht',
    description: 'Persbericht voor media',
    color: '#607D8B',
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8z"/></svg>,
  },
};

const SITUATION_OPTIONS: { value: SituationType; label: string }[] = [
  { value: 'works', label: 'Werkzaamheden' },
  { value: 'hinder', label: 'Geplande hinder' },
  { value: 'progress', label: 'Voortgangsupdate' },
  { value: 'milestone', label: 'Mijlpaal / positief nieuws' },
  { value: 'accessibility', label: 'Bereikbaarheidswijziging' },
  { value: 'meeting', label: 'Uitnodiging bijeenkomst' },
  { value: 'decision', label: 'Formeel besluit' },
  { value: 'urgent', label: 'Urgent bericht' },
  { value: 'participation', label: 'Participatiemoment' },
  { value: 'feedback', label: 'Terugkoppeling participatie' },
];

const GOAL_OPTIONS: { value: CommunicationGoal; label: string }[] = [
  { value: 'inform', label: 'Informeren' },
  { value: 'prepare', label: 'Bewoners voorbereiden' },
  { value: 'warn', label: 'Waarschuwen' },
  { value: 'reduce_concerns', label: 'Zorgen wegnemen' },
  { value: 'understanding', label: 'Begrip creeren' },
  { value: 'invite_participation', label: 'Participatie uitnodigen' },
  { value: 'collect_input', label: 'Input ophalen' },
  { value: 'share_results', label: 'Resultaten delen' },
  { value: 'accountability', label: 'Verantwoording' },
];

const IMPACT_OPTIONS: { value: ImpactLevel; label: string; color: string }[] = [
  { value: 'low', label: 'Laag (informatief)', color: 'var(--color-success)' },
  { value: 'moderate', label: 'Matig', color: 'var(--color-warning)' },
  { value: 'strong', label: 'Sterk (hinder)', color: '#FF6B35' },
  { value: 'politically_sensitive', label: 'Politiek gevoelig', color: '#9C27B0' },
  { value: 'urgent_crisis', label: 'Urgent / crisis', color: 'var(--color-error)' },
];

// ── Analysis helpers ───────────────────────────────────────────────────────

function analyzeProjectData(data: ProjectData): SmartContext {
  // Detect situation
  let situation: SituationType = 'progress';
  let situationReason = 'Standaard voortgangsupdate';

  if (data.tasks?.length > 0) {
    const now = new Date();
    const upcoming = data.tasks.filter((t: any) => {
      const start = t.startDate ? new Date(t.startDate) : null;
      return start && start > now && start < new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    });
    if (upcoming.some((t: any) => t.title?.toLowerCase().includes('nacht') || t.description?.toLowerCase().includes('nacht'))) {
      situation = 'hinder';
      situationReason = 'Nachtelijke werkzaamheden gepland';
    } else if (upcoming.length > 0) {
      situation = 'works';
      situationReason = `${upcoming.length} werkzaamheden gepland komende 30 dagen`;
    }
  }

  if (data.sentiment) {
    const entries = Array.isArray(data.sentiment) ? data.sentiment : [data.sentiment];
    const maxEsc = Math.max(...entries.map((s: any) => s.analysis?.overallEscalationLevel || 0));
    if (maxEsc >= 4) {
      situation = 'urgent';
      situationReason = `Hoog escalatieniveau (${maxEsc}/5) in sentiment`;
    }
  }

  // Detect goal
  const goalMap: Record<SituationType, CommunicationGoal> = {
    works: 'prepare', hinder: 'warn', progress: 'inform', milestone: 'share_results',
    accessibility: 'warn', meeting: 'invite_participation', decision: 'inform',
    urgent: 'warn', participation: 'invite_participation', feedback: 'share_results',
  };
  let goal = goalMap[situation];
  let goalReason = `Past bij situatie "${SITUATION_OPTIONS.find(s => s.value === situation)?.label}"`;

  if (data.strategies?.length > 0) {
    const s = data.strategies[0];
    if (s.objectives?.some((o: any) => (o.title || '').toLowerCase().includes('draagvlak'))) {
      goal = 'understanding';
      goalReason = `Strategiedoel: draagvlak opbouwen`;
    }
  }

  // Detect impact
  let impact: ImpactLevel = 'low';
  let impactReason = 'Standaard informatief niveau';

  if (situation === 'hinder' || situation === 'works') {
    impact = 'moderate';
    impactReason = 'Werkzaamheden hebben invloed op omgeving';
  }
  if (situation === 'urgent') {
    impact = 'urgent_crisis';
    impactReason = 'Spoedsituatie gedetecteerd';
  }
  if (data.sentiment) {
    const entries = Array.isArray(data.sentiment) ? data.sentiment : [data.sentiment];
    const maxEsc = Math.max(...entries.map((s: any) => s.analysis?.overallEscalationLevel || 0));
    if (maxEsc >= 3 && impact === 'low') {
      impact = 'moderate';
      impactReason = `Sentiment escalatieniveau ${maxEsc}/5`;
    }
  }

  // Suggested tools (cast to string for comparison since some values are only set by user)
  const situationStr = situation as string;
  const impactStr = impact as string;
  const isHighImpact = impactStr === 'strong' || impactStr === 'urgent_crisis' || impactStr === 'politically_sensitive';
  const hasElderly = (data.gebiedsanalyse?.demographics?.percentage65Plus || 0) > 20;
  const suggestedTools: SmartContext['suggestedTools'] = [];

  suggestedTools.push({ id: 'website', reason: 'Centraal informatiepunt', primary: true });
  if (isHighImpact || hasElderly || situation === 'works' || situation === 'hinder') {
    suggestedTools.push({ id: 'brief', reason: hasElderly ? 'Offline kanaal (veel 65+ in gebied)' : 'Formele communicatie bij impact', primary: true });
  }
  if (impact === 'urgent_crisis' || situation === 'urgent' || situation === 'hinder') {
    suggestedTools.push({ id: 'sms', reason: 'Direct bereik bij urgente situatie', primary: true });
  }
  if (goal === 'share_results' || goal === 'invite_participation' || situation === 'progress') {
    suggestedTools.push({ id: 'email', reason: 'Digitale nieuwsbrief voor breed bereik', primary: true });
  }
  suggestedTools.push({ id: 'socialmedia', reason: 'Online zichtbaarheid en bereik', primary: !isHighImpact });
  if (!suggestedTools.find(t => t.id === 'email')) {
    suggestedTools.push({ id: 'email', reason: 'Mailinglijst bereiken', primary: false });
  }
  if (isHighImpact || situation === 'works') {
    suggestedTools.push({ id: 'infographic', reason: 'Complexe info visueel maken', primary: false });
  }
  if (situationStr === 'milestone' || situationStr === 'decision' || isHighImpact) {
    suggestedTools.push({ id: 'persbericht', reason: 'Mediaverspreiding bij nieuwswaardig moment', primary: situationStr === 'milestone' || situationStr === 'decision' });
  }

  // Target group hints
  const targetGroupHints: string[] = [];
  if (data.gebiedsanalyse?.demographics) {
    const demo = data.gebiedsanalyse.demographics;
    if (demo.percentage65Plus > 20) targetGroupHints.push(`Veel ouderen in gebied (${Math.round(demo.percentage65Plus)}% is 65+) - overweeg offline middelen`);
    if (demo.bevolkingsdichtheid > 5000) targetGroupHints.push(`Hoge bevolkingsdichtheid - breed communiceren`);
  }
  if (data.gebiedsanalyse?.additionalDemographics?.percentageNietWesters > 15) {
    targetGroupHints.push(`Meertalige communicatie overwegen (${Math.round(data.gebiedsanalyse.additionalDemographics.percentageNietWesters)}% niet-westers)`);
  }
  if (data.relationships?.length > 0) {
    targetGroupHints.push(`${data.relationships.length || 0} stakeholders in relatiebeheer`);
  }
  if (data.gebiedsanalyse?.bedrijvenOverpass?.totaal > 0 || data.gebiedsanalyse?.bedrijven?.totaal > 0) {
    const total = data.gebiedsanalyse?.bedrijvenOverpass?.totaal || data.gebiedsanalyse?.bedrijven?.totaal || 0;
    targetGroupHints.push(`${total} bedrijven in het gebied - ondernemers informeren`);
  }

  // Missing info
  const missingInfo: MissingInfo[] = [];
  if (!data.tasks?.length) missingInfo.push({ field: 'Werkplanning (data/deadlines)', source: 'Werkplanning' });
  if (!data.gebiedsanalyse?.demographics) missingInfo.push({ field: 'Demografische gegevens', source: 'Gebiedsanalyse' });
  if (!data.personas?.length) missingInfo.push({ field: 'Persona-informatie', source: "Persona's" });
  if (!data.relationships?.length) missingInfo.push({ field: 'Contactpersoon project', source: 'Relatiebeheer' });
  if (situation === 'hinder' || situation === 'works') {
    missingInfo.push({ field: 'Exacte geluidsbelasting (dB)', source: 'Werkplanning' });
  }

  // Content blocks
  const contentBlocks = ['Wat gaat er gebeuren', 'Wanneer', 'Waar', 'Waarom', 'Contactgegevens', 'Volgende update'];
  if (situation === 'works' || situation === 'hinder') {
    contentBlocks.splice(4, 0, 'Wat bewoners gaan merken', 'Hoe hinder wordt beperkt');
  }
  if (isHighImpact) contentBlocks.push('Compensatie');
  if (data.questions?.length > 0) contentBlocks.push('FAQ');

  return { situation, situationReason, goal, goalReason, impact, impactReason, suggestedTools, targetGroupHints, missingInfo, contentBlocks };
}

// ── Main Component ─────────────────────────────────────────────────────────

export const MiddelenModule: React.FC<MiddelenModuleProps> = ({ project }) => {
  const { user } = useAuth();
  const toast = useToast();

  const [mode, setMode] = useState<ViewMode>('choose');
  const [loading, setLoading] = useState(true);
  const [projectData, setProjectData] = useState<ProjectData>({
    gebiedsanalyse: null, personas: [], strategies: [], tasks: [],
    questions: [], relationships: [], sentiment: null, media: [],
  });

  // Smart mode state
  const [situation, setSituation] = useState<SituationType>('progress');
  const [goal, setGoal] = useState<CommunicationGoal>('inform');
  const [impact, setImpact] = useState<ImpactLevel>('low');
  const [extraContext, setExtraContext] = useState('');

  // Shared state
  const [selectedTool, setSelectedTool] = useState<CommunicationTool | null>(null);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);
  const [generatedSections, setGeneratedSections] = useState<{ tool: string; label: string; color: string; icon: React.ReactNode; content: string }[]>([]);
  const [activeTab, setActiveTab] = useState(0);

  // Load project data
  useEffect(() => {
    loadProjectData();
  }, [project.id]);

  const loadProjectData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        analysisAPI.getArea(project.id),
        personasAPI.getByProject(project.id),
        strategyAPI.getByProject(project.id),
        tasksAPI.getByProject(project.id),
        questionsAPI.getByProject(project.id),
        relationshipsAPI.getByProject(project.id),
        sentimentAPI.getByProject(project.id),
        mediaAPI.getByProject(project.id),
      ]);
      setProjectData({
        gebiedsanalyse: results[0].status === 'fulfilled' ? results[0].value : null,
        personas: results[1].status === 'fulfilled' ? results[1].value || [] : [],
        strategies: results[2].status === 'fulfilled' ? results[2].value || [] : [],
        tasks: results[3].status === 'fulfilled' ? results[3].value || [] : [],
        questions: results[4].status === 'fulfilled' ? (results[4].value?.questions || results[4].value || []) : [],
        relationships: results[5].status === 'fulfilled' ? (results[5].value?.relationships || results[5].value || []) : [],
        sentiment: results[6].status === 'fulfilled' ? (results[6].value?.sentimentEntries || results[6].value) : null,
        media: results[7].status === 'fulfilled' ? (results[7].value?.media || results[7].value || []) : [],
      });
    } catch (err) {
      console.error('Failed to load project data:', err);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  // Smart analysis
  const smartContext = useMemo(() => analyzeProjectData(projectData), [projectData]);

  // Sync smart defaults when analysis completes
  useEffect(() => {
    if (!loading) {
      setSituation(smartContext.situation);
      setGoal(smartContext.goal);
      setImpact(smartContext.impact);
    }
  }, [loading, smartContext.situation, smartContext.goal, smartContext.impact]);

  // Parse AI response into sections per tool
  const parseToolSections = (content: string, tools: { id: CommunicationTool; label: string }[]): { tool: string; label: string; color: string; icon: React.ReactNode; content: string }[] => {
    const sections: { tool: string; label: string; color: string; icon: React.ReactNode; content: string }[] = [];

    // Try to split by ## MIDDEL: headers
    const pattern = /##\s*(?:MIDDEL:\s*)?(.+?)(?:\n|$)/g;
    const headers: { label: string; index: number }[] = [];
    let match;
    while ((match = pattern.exec(content)) !== null) {
      headers.push({ label: match[1].trim(), index: match.index });
    }

    if (headers.length >= 2) {
      // Multi-section content
      for (let i = 0; i < headers.length; i++) {
        const start = headers[i].index + content.slice(headers[i].index).indexOf('\n') + 1;
        const end = i + 1 < headers.length ? headers[i + 1].index : content.length;
        const sectionContent = content.slice(start, end).trim();
        const headerLabel = headers[i].label.replace(/\*\*/g, '').trim();

        // Match to tool definition
        const matchedTool = tools.find(t => headerLabel.toLowerCase().includes(t.label.toLowerCase()));
        const toolId = matchedTool?.id || ('custom_' + i);
        const toolDef = matchedTool ? TOOLS[matchedTool.id] : null;

        sections.push({
          tool: toolId,
          label: toolDef?.label || headerLabel,
          color: toolDef?.color || 'var(--color-content-primary)',
          icon: toolDef?.icon || <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>,
          content: sectionContent,
        });
      }
    }

    // Fallback: single section
    if (sections.length === 0) {
      const toolId = tools[0]?.id || 'general';
      const toolDef = tools[0] ? TOOLS[tools[0].id] : null;
      sections.push({
        tool: toolId,
        label: toolDef?.label || 'Content',
        color: toolDef?.color || 'var(--color-content-primary)',
        icon: toolDef?.icon || <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>,
        content: content,
      });
    }

    return sections;
  };

  // Generate content
  const handleGenerate = async (tool?: CommunicationTool) => {
    setGeneratingContent(true);
    setGeneratedContent(null);
    setGeneratedSections([]);
    setActiveTab(0);

    const toolList = tool
      ? [{ id: tool, label: TOOLS[tool].label }]
      : smartContext.suggestedTools.map(t => ({ id: t.id, label: TOOLS[t.id].label }));
    const toolLabel = toolList.map(t => t.label).join(', ');
    const sitLabel = SITUATION_OPTIONS.find(s => s.value === situation)?.label || situation;
    const goalLabel = GOAL_OPTIONS.find(g => g.value === goal)?.label || goal;

    const multiTool = toolList.length > 1;

    const prompt = `Genereer communicatie-content voor het project "${project.name}" in ${project.location}.

SITUATIE: ${sitLabel}
COMMUNICATIEDOEL: ${goalLabel}
IMPACT: ${IMPACT_OPTIONS.find(i => i.value === impact)?.label || impact}
${extraContext ? `EXTRA CONTEXT: ${extraContext}` : ''}

${multiTool ? `AANBEVOLEN MIDDELEN: ${toolLabel}

Genereer voor ELK middel een apart blok. Begin elk blok met een ## header in dit formaat:
## MIDDEL: [naam middel]

Genereer per middel een complete concepttekst.` : `GEVRAAGD MIDDEL: ${TOOLS[tool!].label} - ${TOOLS[tool!].description}

Genereer een complete concepttekst voor dit middel. Begin met:
## MIDDEL: ${TOOLS[tool!].label}`}

Gebruik taalniveau B1. Wees beknopt maar volledig.
Markeer ontbrekende informatie met [ONTBREEKT: beschrijving].

${projectData.personas?.length > 0 ? `PERSONA'S: ${projectData.personas.map((p: any) => `${p.name} (${p.age}j, ${p.occupation})`).join(', ')}` : ''}
${projectData.questions?.length > 0 ? `VEELGESTELDE VRAGEN: ${projectData.questions.slice(0, 5).map((q: any) => q.question || q.title).join('; ')}` : ''}
${smartContext.contentBlocks.length > 0 ? `CONTENTBLOKKEN: ${smartContext.contentBlocks.join(', ')}` : ''}`;

    try {
      const response = await aiAssistantAPI.chat(project.id, prompt, []);
      setGeneratedContent(response.reply);
      const sections = parseToolSections(response.reply, toolList);
      setGeneratedSections(sections);
    } catch (err) {
      console.error('Generation failed:', err);
      toast.error('Fout', 'Kon content niet genereren');
    } finally {
      setGeneratingContent(false);
    }
  };

  const handleCopyAll = () => {
    if (generatedContent) {
      navigator.clipboard.writeText(generatedContent);
      toast.success('Gekopieerd', 'Alle content naar klembord gekopieerd');
    }
  };

  const handleCopySection = (index: number) => {
    if (generatedSections[index]) {
      navigator.clipboard.writeText(generatedSections[index].content);
      toast.success('Gekopieerd', `${generatedSections[index].label} naar klembord gekopieerd`);
    }
  };

  const handleReset = () => {
    setSelectedTool(null);
    setGeneratedContent(null);
    setGeneratedSections([]);
    setGeneratingContent(false);
    setActiveTab(0);
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className={styles.middelen}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Projectdata analyseren...</p>
        </div>
      </div>
    );
  }

  // ── Render ──
  return (
    <div className={styles.middelen}>
      <div className={styles.header}>
        <div>
          <h1>Middelen</h1>
          <p className={styles.subtitle}>Genereer communicatiemiddelen op basis van projectcontext</p>
        </div>
      </div>

      {/* Mode Toggle */}
      <div className={styles.modeToggle}>
        <button
          className={`${styles.modeButton} ${mode === 'choose' ? styles.active : ''}`}
          onClick={() => { setMode('choose'); handleReset(); }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg>
          Kies een middel
        </button>
        <button
          className={`${styles.modeButton} ${mode === 'smart' ? styles.active : ''}`}
          onClick={() => { setMode('smart'); handleReset(); }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
          Slim advies
        </button>
      </div>

      {/* ── CHOOSE MODE ── */}
      {mode === 'choose' && !selectedTool && (
        <div className={styles.toolsGrid}>
          {(Object.entries(TOOLS) as [CommunicationTool, typeof TOOLS[CommunicationTool]][]).map(([id, tool]) => {
            const suggestion = smartContext.suggestedTools.find(s => s.id === id);
            return (
              <button
                key={id}
                className={`${styles.toolCard} ${suggestion?.primary ? styles.suggested : ''}`}
                onClick={() => setSelectedTool(id)}
              >
                <div className={styles.toolAvatar} style={{ background: tool.color }}>
                  {tool.icon}
                </div>
                <h4>{tool.label}</h4>
                <p>{tool.description}</p>
                {suggestion && (
                  <span className={`${styles.badge} ${suggestion.primary ? styles.badgePrimary : styles.badgeSecondary}`}>
                    {suggestion.primary ? 'Aanbevolen' : 'Optioneel'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ── CHOOSE MODE: Selected tool ── */}
      {mode === 'choose' && selectedTool && (
        <div className={styles.selectedToolView}>
          <button className={styles.backButton} onClick={handleReset}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            Terug naar overzicht
          </button>

          <div className={styles.selectedToolHeader}>
            <div className={styles.toolAvatar} style={{ background: TOOLS[selectedTool].color }}>
              {TOOLS[selectedTool].icon}
            </div>
            <div>
              <h2>{TOOLS[selectedTool].label}</h2>
              <p>{TOOLS[selectedTool].description}</p>
            </div>
          </div>

          {/* Context hints */}
          {smartContext.targetGroupHints.length > 0 && (
            <div className={styles.hintsBox}>
              <h4>Doelgroep inzichten uit projectdata</h4>
              <ul>
                {smartContext.targetGroupHints.map((hint, i) => <li key={i}>{hint}</li>)}
              </ul>
            </div>
          )}

          {smartContext.missingInfo.length > 0 && (
            <div className={styles.warningBox}>
              <h4>Ontbrekende informatie</h4>
              {smartContext.missingInfo.map((info, i) => (
                <div key={i} className={styles.warningItem}>
                  <span className={styles.warningField}>{info.field}</span>
                  <span className={styles.warningSource}>{info.source}</span>
                </div>
              ))}
            </div>
          )}

          {/* Extra context */}
          <div className={styles.contextInput}>
            <label>Extra context (optioneel)</label>
            <textarea
              value={extraContext}
              onChange={(e) => setExtraContext(e.target.value)}
              placeholder="Beschrijf de specifieke situatie, data, locatie of andere details..."
              rows={3}
            />
          </div>

          {/* Generate */}
          {!generatedContent && !generatingContent && (
            <Button variant="primary" onClick={() => handleGenerate(selectedTool)}>
              Genereer {TOOLS[selectedTool].label}
            </Button>
          )}

          {generatingContent && (
            <div className={styles.generating}>
              <div className={styles.spinner} />
              <p>Content wordt gegenereerd...</p>
            </div>
          )}

          {generatedContent && (
            <GeneratedContentView
              sections={generatedSections}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              onCopyAll={handleCopyAll}
              onCopySection={handleCopySection}
              onReset={handleReset}
              onRegenerate={() => handleGenerate(selectedTool)}
            />
          )}
        </div>
      )}

      {/* ── SMART MODE ── */}
      {mode === 'smart' && (
        <div className={styles.smartLayout}>
          {/* Left: context controls */}
          <div className={styles.smartControls}>
            <div className={styles.controlCard}>
              <h3>Projectanalyse</h3>

              <div className={styles.controlGroup}>
                <label>Situatie</label>
                <div className={styles.selectWithHint}>
                  <select value={situation} onChange={(e) => setSituation(e.target.value as SituationType)}>
                    {SITUATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {smartContext.situationReason && (
                    <span className={styles.hint}>{smartContext.situationReason}</span>
                  )}
                </div>
              </div>

              <div className={styles.controlGroup}>
                <label>Communicatiedoel</label>
                <div className={styles.selectWithHint}>
                  <select value={goal} onChange={(e) => setGoal(e.target.value as CommunicationGoal)}>
                    {GOAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {smartContext.goalReason && (
                    <span className={styles.hint}>{smartContext.goalReason}</span>
                  )}
                </div>
              </div>

              <div className={styles.controlGroup}>
                <label>Impact</label>
                <div className={styles.impactSelector}>
                  {IMPACT_OPTIONS.map(o => (
                    <button
                      key={o.value}
                      className={`${styles.impactChip} ${impact === o.value ? styles.active : ''}`}
                      onClick={() => setImpact(o.value)}
                    >
                      <span className={styles.impactDot} style={{ background: o.color }} />
                      {o.label}
                    </button>
                  ))}
                </div>
                {smartContext.impactReason && (
                  <span className={styles.hint}>{smartContext.impactReason}</span>
                )}
              </div>

              <div className={styles.controlGroup}>
                <label>Extra context (optioneel)</label>
                <textarea
                  value={extraContext}
                  onChange={(e) => setExtraContext(e.target.value)}
                  placeholder="Aanvullende details..."
                  rows={2}
                />
              </div>
            </div>

            {/* Target group hints */}
            {smartContext.targetGroupHints.length > 0 && (
              <div className={styles.controlCard}>
                <h3>Doelgroep inzichten</h3>
                <ul className={styles.hintsList}>
                  {smartContext.targetGroupHints.map((hint, i) => <li key={i}>{hint}</li>)}
                </ul>
              </div>
            )}

            {/* Data sources */}
            <div className={styles.controlCard}>
              <h3>Databronnen</h3>
              <div className={styles.sourcesList}>
                <SourceDot label="Gebiedsanalyse" loaded={!!projectData.gebiedsanalyse} />
                <SourceDot label="Sentiment" loaded={!!projectData.sentiment} />
                <SourceDot label="Persona's" loaded={projectData.personas.length > 0} />
                <SourceDot label="Strategie" loaded={projectData.strategies.length > 0} />
                <SourceDot label="Werkplanning" loaded={projectData.tasks.length > 0} />
                <SourceDot label="Vragen" loaded={projectData.questions.length > 0} />
                <SourceDot label="Relatiebeheer" loaded={projectData.relationships.length > 0} />
                <SourceDot label="Media" loaded={projectData.media.length > 0} />
              </div>
            </div>
          </div>

          {/* Right: advice result */}
          <div className={styles.smartResult}>
            {!generatedContent && !generatingContent && (
              <>
                {/* Recommended tools */}
                <div className={styles.adviceCard}>
                  <h3>Aanbevolen communicatiemix</h3>
                  <div className={styles.adviceTools}>
                    {smartContext.suggestedTools.filter(t => t.primary).map(t => (
                      <div key={t.id} className={styles.adviceToolPrimary}>
                        <div className={styles.adviceToolIcon} style={{ background: TOOLS[t.id].color }}>
                          {TOOLS[t.id].icon}
                        </div>
                        <div>
                          <strong>{TOOLS[t.id].label}</strong>
                          <span>{t.reason}</span>
                        </div>
                      </div>
                    ))}
                    {smartContext.suggestedTools.filter(t => !t.primary).map(t => (
                      <div key={t.id} className={styles.adviceToolSecondary}>
                        <div className={styles.adviceToolIconSmall} style={{ borderColor: TOOLS[t.id].color }}>
                          {TOOLS[t.id].icon}
                        </div>
                        <div>
                          <strong>{TOOLS[t.id].label}</strong>
                          <span>{t.reason}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Content blocks */}
                <div className={styles.adviceCard}>
                  <h3>Contentblokken</h3>
                  <div className={styles.blockChips}>
                    {smartContext.contentBlocks.map((block, i) => (
                      <span key={i} className={styles.blockChip}>{block}</span>
                    ))}
                  </div>
                </div>

                {/* Missing info */}
                {smartContext.missingInfo.length > 0 && (
                  <div className={styles.warningBox}>
                    <h4>Ontbrekende informatie</h4>
                    {smartContext.missingInfo.map((info, i) => (
                      <div key={i} className={styles.warningItem}>
                        <span className={styles.warningField}>{info.field}</span>
                        <span className={styles.warningSource}>{info.source}</span>
                      </div>
                    ))}
                  </div>
                )}

                <Button variant="primary" onClick={() => handleGenerate()}>
                  Genereer alle content
                </Button>
              </>
            )}

            {generatingContent && (
              <div className={styles.generating}>
                <div className={styles.spinner} />
                <p>Content wordt gegenereerd op basis van alle projectdata...</p>
              </div>
            )}

            {generatedContent && (
              <GeneratedContentView
                sections={generatedSections}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onCopyAll={handleCopyAll}
                onCopySection={handleCopySection}
                onReset={handleReset}
                onRegenerate={() => handleGenerate()}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────

const SourceDot: React.FC<{ label: string; loaded: boolean }> = ({ label, loaded }) => (
  <div className={styles.sourceDot}>
    <span className={`${styles.dot} ${loaded ? styles.dotLoaded : styles.dotEmpty}`} />
    <span>{label}</span>
  </div>
);

function formatContent(raw: string): string {
  return raw
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>')
    .replace(/^/, '<p>')
    .replace(/$/, '</p>')
    .replace(/####\s(.*?)(?=<br|<\/p>)/g, '<h5>$1</h5>')
    .replace(/###\s(.*?)(?=<br|<\/p>)/g, '<h4>$1</h4>')
    .replace(/##\s(?:MIDDEL:\s*)?(.*?)(?=<br|<\/p>)/g, '<h3>$1</h3>')
    .replace(/#\s(.*?)(?=<br|<\/p>)/g, '<h2>$1</h2>')
    .replace(/\[ONTBREEKT:(.*?)\]/g, '<span class="missing-inline">ONTBREEKT:$1</span>');
}

const GeneratedContentView: React.FC<{
  sections: { tool: string; label: string; color: string; icon: React.ReactNode; content: string }[];
  activeTab: number;
  onTabChange: (index: number) => void;
  onCopyAll: () => void;
  onCopySection: (index: number) => void;
  onReset: () => void;
  onRegenerate: () => void;
}> = ({ sections, activeTab, onTabChange, onCopyAll, onCopySection, onReset, onRegenerate }) => {
  if (sections.length === 0) return null;

  const hasTabs = sections.length > 1;

  return (
    <div className={styles.generatedContent}>
      <div className={styles.generatedHeader}>
        <h3>Gegenereerde content</h3>
        {hasTabs && (
          <button className={styles.copyAllButton} onClick={onCopyAll}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
            Kopieer alles
          </button>
        )}
      </div>

      {/* Tab navigation */}
      {hasTabs && (
        <div className={styles.contentTabs}>
          {sections.map((section, i) => (
            <button
              key={i}
              className={`${styles.contentTab} ${activeTab === i ? styles.contentTabActive : ''}`}
              onClick={() => onTabChange(i)}
            >
              <span className={styles.contentTabIcon} style={{ background: section.color }}>
                {section.icon}
              </span>
              {section.label}
            </button>
          ))}
        </div>
      )}

      {/* Active tab content */}
      <div className={styles.contentOutput} dangerouslySetInnerHTML={{
        __html: formatContent(sections[activeTab]?.content || '')
      }} />

      <div className={styles.contentActions}>
        <Button variant="primary" onClick={() => onCopySection(activeTab)}>
          Kopieer {sections[activeTab]?.label || 'content'}
        </Button>
        <Button variant="outline" onClick={onRegenerate}>Opnieuw genereren</Button>
        <Button variant="outline" onClick={onReset}>Terug</Button>
      </div>
    </div>
  );
};

export default MiddelenModule;
